import {StaticDecode, TUnsafe} from "typebox";
import {Dispatch, ReactNode, SetStateAction, useMemo} from "react";
import {FacetCount, FilterClause, IValue} from "platformed-client/types";
import {Value} from "typebox/value";
import {Type} from "../../utils/typing";
import {useSessionState} from "../sessionState";
import {typedEntries} from "../../utils/typescript";
import * as _ from "lodash-es";
import {AnyFilterDefinition, AnyFilterDefinitions, FilterConfig} from "./filterConfig";
import {BackendFilterEncoders, FilterPredicates} from "./filterPredicate";

// The filter operator determines how multiple filters are combined.
export const FILTER_OPERATOR_SCHEMA = Type.Union([Type.Literal("all"), Type.Literal("any")]);
export type FilterOperator = StaticDecode<typeof FILTER_OPERATOR_SCHEMA>;

// The condition for a filter determines how the filter value is applied to the
// data.
export type Condition<ConditionValue extends string, FilterValue> = {
  value: ConditionValue;
  label: ReactNode | ((filterValue: FilterValue) => ReactNode);
};
export type AnyCondition = Condition<string, any>;

// The value used by a filter for comparisons.
export type FilterValue<D extends AnyFilterDefinition> = StaticDecode<D["schema"]>;

// A filter consists of a condition and a value.
export type FilterFromParts<FilterValue, ConditionValue extends string> = {
  condition: ConditionValue;
  value: FilterValue;
};
export type Filter<D extends AnyFilterDefinition> = NonNullable<D["components"]["filter"]>;
export type AnyFilter = FilterFromParts<any, string>;

// A filter and its kind, strongly typed based on the filter definitions.
export type FilterEntry<Ds extends AnyFilterDefinitions, K extends keyof Ds> = {kind: K; filter: Filter<Ds[K]>};
export type AnyFilterEntry = {kind: string; filter: AnyFilter};

/**
 * A strongly typed array of filters (an array of the union of all the types of
 * filters for a particular set of definitions).
 */
export type FilterEntries<Ds extends AnyFilterDefinitions> = {
  [K in keyof Ds]: FilterEntry<Ds, K>;
}[keyof Ds][];

export type FilterState<Ds extends AnyFilterDefinitions> = {
  /** The filter operator determines how multiple filter entries are combined. */
  operator: FilterOperator;

  /**
   * Filter entries, excluding those that have empty values. Use this when
   * applying filtering logic.
   */
  entries: FilterEntries<Ds>;
};

/**
 * Given a filter schema and value, return whether the value is considered
 * "empty" (same as the default of the schema).
 */
export function isFilterValueEmpty<Ds extends AnyFilterDefinitions, K extends keyof Ds>(
  definition: Ds[K],
  filter: Filter<Ds[K]>,
): boolean {
  // Boolean values are never "empty" - they're always meaningful (true or false)
  if (typeof filter.value === "boolean") {
    return false;
  }
  // Number values are never "empty" - even 0 is a meaningful value
  if (typeof filter.value === "number") {
    return false;
  }
  const schema = definition.schema;
  const emptyValue = Value.Create(schema);
  return Value.Equal(Value.Encode(schema, filter.value), emptyValue);
}

export function createEmptyFilter<D extends AnyFilterDefinition>(definition: D): Filter<D> {
  return {
    value: Value.Decode(definition.schema, Value.Create(definition.schema)),
    condition: definition.conditions[0].value,
  };
}

/**
 * Constructs a schema from a set of filter definitions to be used to encode /
 * decode the filter state to / from session storage.
 */
export function constructFilterSchema<const Ds extends AnyFilterDefinitions>(
  definitions: Ds,
): TUnsafe<FilterState<Ds>> {
  return Type.Object({
    operator: FILTER_OPERATOR_SCHEMA,
    entries: Type.Array(
      Type.Union(
        Object.entries(definitions).map(([key, definition]) =>
          Type.Object({
            kind: Type.Literal(key),
            filter: Type.Object({
              condition: Type.Union(definition.conditions.map(c => Type.Literal(c.value))),
              value: definition.schema,
            }),
          }),
        ),
      ),
    ),
  }) as any;
}

export function useDefaultFilterState<const Ds extends AnyFilterDefinitions>(
  config: FilterConfig<Ds>,
): FilterState<Ds> {
  return useMemo(() => ({operator: "all", entries: config.defaultFilters ?? []}) as const, [config.defaultFilters]);
}

// Hook to manage filter state with session storage persistence.
export function useFilterState<const Ds extends AnyFilterDefinitions>(
  config: FilterConfig<Ds>,
): [FilterState<Ds>, Dispatch<SetStateAction<FilterState<Ds>>>] {
  const schema = useMemo(() => constructFilterSchema(config.filters), [config.filters]);
  const defaultFilter = useDefaultFilterState(config);
  const sessionStorageKey = useMemo(() => `filterState.${config.key}`, [config.key]);

  return useSessionState(sessionStorageKey, schema, defaultFilter);
}

// Applies the filters in the given state to the provided items using the
// provided predicates. Use `useFiltered` if you need the facet counts as well.
export function applyFilters<Item, Ds extends AnyFilterDefinitions>(
  items: Item[],
  state: FilterState<Ds>,
  predicates: FilterPredicates<Item, Ds>,
  // Optional kind of filter to exclude from application
  exclude?: keyof Ds,
): Item[] {
  if (state.entries.length === 0) return items;

  return items.filter(item => {
    const fn = (state.operator === "all" ? state.entries.every : state.entries.some).bind(state.entries);
    return fn(entry => entry.kind === exclude || predicates[entry.kind](item, entry.filter));
  });
}

// Hook to apply filtering to a set of items based on the provided filter state
// and predicates.
export function useFiltered<Item, Ds extends AnyFilterDefinitions>(
  items: Item[],
  state: FilterState<Ds>,
  predicates: FilterPredicates<Item, Ds>,
  search?: (item: Item) => boolean,
) {
  return useMemo(() => {
    const searchedItems = search ? items.filter(search) : items;
    const filtered = applyFilters(searchedItems, state, predicates);

    const facetCounts: {
      [K in keyof Ds]?: FacetCount[];
    } = {};

    typedEntries(predicates)
      .filter(item => !!item[1].extractFacetValues)
      .forEach(([key, predicate]) => {
        const partiallyFiltered = applyFilters(searchedItems, state, predicates, key);
        const counts: FacetCount[] = [];
        for (const item of partiallyFiltered) {
          const facetValues = predicate.extractFacetValues!(item);
          for (const facetValue of facetValues) {
            const existing = counts.find(c => _.isEqual(c.value, facetValue));
            if (existing) {
              existing.count += 1;
            } else {
              counts.push({value: facetValue as IValue, count: 1});
            }
          }
        }

        facetCounts[key] = counts;
      });

    return {filtered, facetCounts};
  }, [items, state, predicates, search]);
}

/**
 * Encodes the current filter state into a FilterClause to be sent to the backend.
 */
export function encodeFilters<Ds extends AnyFilterDefinitions>(
  state: FilterState<Ds>,
  encoders: BackendFilterEncoders<Ds>,
  searchState?: {queries: string[]},
): FilterClause | undefined {
  const filterClause: FilterClause | undefined =
    state.entries.length === 0
      ? undefined
      : {
          kind: "Composite",
          operator: state.operator === "all" ? "All" : "Any",
          clauses: state.entries.map(entry => encoders[entry.kind](entry.filter)),
        };
  const searchClauses: FilterClause[] =
    searchState?.queries
      .filter(q => q.trim() !== "")
      .map(query => ({
        kind: "Expression",
        field: "search",
        negated: false,
        condition: "Contains",
        value: {kind: "String", value: query},
      })) ?? [];

  if (searchClauses.length === 0) {
    return filterClause;
  } else if (!filterClause) {
    return {kind: "Composite", operator: "All", clauses: searchClauses};
  } else {
    return {kind: "Composite", operator: "All", clauses: [filterClause, ...searchClauses]};
  }
}
